/**
 * Calendar Component for Shifts and Vacation Management
 * 
 * Displays a week view calendar with the ability to navigate between weeks.
 */

import React, { useState } from 'react';
import {
  Box,
  Paper,
  Grid,
  Typography,
  IconButton,
  Card,
  CardContent,
  Chip,
  Tooltip,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { getCurrentWeekRange, formatDateToAPI } from '../utils/shiftUtils';
import { HOLIDAYS, COUNTRY_FLAGS } from '../config/shiftConfig';

const CalendarWeekView = ({
  onDateSelect,
  onWeekChange,
  assignments = [],
  timeOffs = [],
  readonly = false,
  height = 'auto',
}) => {
  const [currentWeek, setCurrentWeek] = useState(() => getCurrentWeekRange());
  
  // Get holidays for a specific date
  const getHolidaysForDate = (date) => {
    const dateStr = formatDateToAPI(date);
    return HOLIDAYS.filter(h => h.date === dateStr);
  };

  // Group holidays by name and collect country flags
  const getGroupedHolidaysByName = (holidays) => {
    const grouped = {};
    holidays.forEach(holiday => {
      if (!grouped[holiday.name]) {
        grouped[holiday.name] = [];
      }
      grouped[holiday.name].push(holiday.country);
    });
    return Object.entries(grouped).map(([name, countries]) => ({
      name,
      countries,
      flags: countries.map(c => COUNTRY_FLAGS[c]).join(' ')
    }));
  };
  
  // Move to previous week
  const handlePreviousWeek = () => {
    const prevDate = new Date(currentWeek.start);
    prevDate.setDate(prevDate.getDate() - 7);
    const newWeek = getCurrentWeekRange(prevDate);
    setCurrentWeek(newWeek);
    onWeekChange?.(newWeek);
  };
  
  // Move to next week
  const handleNextWeek = () => {
    const nextDate = new Date(currentWeek.start);
    nextDate.setDate(nextDate.getDate() + 7);
    const newWeek = getCurrentWeekRange(nextDate);
    setCurrentWeek(newWeek);
    onWeekChange?.(newWeek);
  };
  
  // Go to current week
  const handleToday = () => {
    const today = getCurrentWeekRange(new Date());
    setCurrentWeek(today);
    onWeekChange?.(today);
  };
  
  // Get assignments for a specific date
  const getAssignmentsForDate = (date) => {
    const dateStr = formatDateToAPI(date);
    return assignments.filter(a => a.assignment_date === dateStr);
  };
  
  // Get time offs for a specific date
  const getTimeOffsForDate = (date) => {
    const dateStr = formatDateToAPI(date);
    return timeOffs.filter(t => {
      const startDate = new Date(t.start_date);
      const endDate = new Date(t.end_date);
      const checkDate = new Date(dateStr);
      return checkDate >= startDate && checkDate <= endDate;
    });
  };
  
  const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  
  return (
    <Paper elevation={3} sx={{ p: 2, height, display: 'flex', flexDirection: 'column', overflow: 'visible' }}>
      {/* Header with navigation */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <IconButton onClick={handlePreviousWeek} size="small">
          <ChevronLeftIcon />
        </IconButton>
        
        <Typography variant="h6">
          {currentWeek.start.toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric', 
            year: 'numeric' 
          })} - {currentWeek.end.toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric', 
            year: 'numeric' 
          })}
        </Typography>
        
        <Box>
          <IconButton onClick={handleToday} size="small" sx={{ mr: 1 }}>
            Today
          </IconButton>
          <IconButton onClick={handleNextWeek} size="small">
            <ChevronRightIcon />
          </IconButton>
        </Box>
      </Box>
      
      {/* Calendar Grid - First Week */}
      <Box sx={{ mb: 3, overflow: 'visible' }}>
        <Typography variant="caption" sx={{ fontWeight: 'bold', mb: 1, display: 'block' }}>
          Week 1
        </Typography>
        <Grid container spacing={1} sx={{ overflow: 'visible' }}>
          {currentWeek.dates.slice(0, 7).map((date, index) => {
            const dateStr = formatDateToAPI(date);
            const dayAssignments = getAssignmentsForDate(date);
            const dayTimeOffs = getTimeOffsForDate(date);
            const dayHolidays = getHolidaysForDate(date);
            const isToday = new Date().toDateString() === date.toDateString();
            
            return (
              <Grid item xs={12} sm={6} md={4} lg={12/7} key={dateStr}>
                {/* Day name header */}
                <Typography variant="caption" sx={{ fontWeight: 'bold', textAlign: 'center', display: 'block', mb: 0.5 }}>
                  {dayNames[index]}
                </Typography>
                
                <Card
                  sx={{
                    minHeight: '120px',
                    cursor: !readonly ? 'pointer' : 'default',
                    border: isToday ? '2px solid' : '1px solid',
                    borderColor: isToday ? 'primary.main' : 'divider',
                    backgroundColor: isToday ? 'rgba(25, 118, 210, 0.05)' : 'background.paper',
                    transition: 'all 0.2s',
                    '&:hover': !readonly && {
                      boxShadow: 3,
                      transform: 'translateY(-2px)',
                    },
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                  onClick={() => !readonly && onDateSelect && onDateSelect(date)}
                >
                  <CardContent sx={{ p: 0.75, '&:last-child': { pb: 0.75 }, flexGrow: 1 }}>
                    {/* Day number Header */}
                    <Box sx={{ mb: 0.5 }}>
                      <Typography variant="body2" color="textSecondary">
                        {date.getDate()}
                      </Typography>
                    </Box>

                    {/* Holidays */}
                    {dayHolidays.length > 0 && (
                      <Box sx={{ mb: 0.5 }}>
                        {getGroupedHolidaysByName(dayHolidays).map((holiday, i) => (
                          <Tooltip key={i} title={holiday.name}>
                            <Typography variant="caption" sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}>
                              {holiday.flags} {holiday.name}
                            </Typography>
                          </Tooltip>
                        ))}
                      </Box>
                    )}
                    
                    {/* Assignments */}
                    <Box sx={{ mb: 0.5, minHeight: '40px' }}>
                      {dayAssignments.length > 0 ? (
                        dayAssignments.map(assignment => (
                          <Chip
                            key={assignment.id}
                            label={assignment.shift_detail?.name || 'Shift'}
                            size="small"
                            variant="outlined"
                            sx={{
                              display: 'block',
                              width: '100%',
                              mb: 0.25,
                              fontSize: '0.7rem',
                            }}
                          />
                        ))
                      ) : (
                        <Typography variant="caption" color="textSecondary">
                          No assignments
                        </Typography>
                      )}
                    </Box>
                    
                    {/* Time Offs */}
                    {dayTimeOffs.length > 0 && (
                      <Box>
                        {dayTimeOffs.map(timeOff => (
                          <Chip
                            key={timeOff.id}
                            label={`${timeOff.user_detail?.first_name || ''} ${timeOff.user_detail?.last_name || ''}`.trim() || timeOff.user_detail?.username || 'User'}
                            size="small"
                            color="error"
                            variant="outlined"
                            sx={{
                              display: 'block',
                              width: '100%',
                              fontSize: '0.7rem',
                              mb: 0.25,
                            }}
                          />
                        ))}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Box>

      {/* Calendar Grid - Second Week */}
      <Box sx={{ overflow: 'visible' }}>
        <Typography variant="caption" sx={{ fontWeight: 'bold', mb: 1, display: 'block' }}>
          Week 2
        </Typography>
        <Grid container spacing={1} sx={{ overflow: 'visible' }}>
          {currentWeek.dates.slice(7, 14).map((date, index) => {
            const dateStr = formatDateToAPI(date);
            const dayAssignments = getAssignmentsForDate(date);
            const dayTimeOffs = getTimeOffsForDate(date);
            const dayHolidays = getHolidaysForDate(date);
            const isToday = new Date().toDateString() === date.toDateString();
            
            return (
              <Grid item xs={12} sm={6} md={4} lg={12/7} key={dateStr}>
                {/* Day name header */}
                <Typography variant="caption" sx={{ fontWeight: 'bold', textAlign: 'center', display: 'block', mb: 0.5 }}>
                  {dayNames[index]}
                </Typography>
                
                <Card
                  sx={{
                    minHeight: '120px',
                    cursor: !readonly ? 'pointer' : 'default',
                    border: isToday ? '2px solid' : '1px solid',
                    borderColor: isToday ? 'primary.main' : 'divider',
                    backgroundColor: isToday ? 'rgba(25, 118, 210, 0.05)' : 'background.paper',
                    transition: 'all 0.2s',
                    '&:hover': !readonly && {
                      boxShadow: 3,
                      transform: 'translateY(-2px)',
                    },
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                  onClick={() => !readonly && onDateSelect && onDateSelect(date)}
                >
                  <CardContent sx={{ p: 0.75, '&:last-child': { pb: 0.75 }, flexGrow: 1 }}>
                    {/* Day number Header */}
                    <Box sx={{ mb: 0.5 }}>
                      <Typography variant="body2" color="textSecondary">
                        {date.getDate()}
                      </Typography>
                    </Box>

                    {/* Holidays */}
                    {dayHolidays.length > 0 && (
                      <Box sx={{ mb: 0.5 }}>
                        {getGroupedHolidaysByName(dayHolidays).map((holiday, i) => (
                          <Tooltip key={i} title={holiday.name}>
                            <Typography variant="caption" sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}>
                              {holiday.flags} {holiday.name}
                            </Typography>
                          </Tooltip>
                        ))}
                      </Box>
                    )}
                    
                    {/* Assignments */}
                    <Box sx={{ mb: 0.5, minHeight: '40px' }}>
                      {dayAssignments.length > 0 ? (
                        dayAssignments.map(assignment => (
                          <Chip
                            key={assignment.id}
                            label={assignment.shift_detail?.name || 'Shift'}
                            size="small"
                            variant="outlined"
                            sx={{
                              display: 'block',
                              width: '100%',
                              mb: 0.25,
                              fontSize: '0.7rem',
                            }}
                          />
                        ))
                      ) : (
                        <Typography variant="caption" color="textSecondary">
                          No assignments
                        </Typography>
                      )}
                    </Box>
                    
                    {/* Time Offs */}
                    {dayTimeOffs.length > 0 && (
                      <Box>
                        {dayTimeOffs.map(timeOff => (
                          <Chip
                            key={timeOff.id}
                            label={`${timeOff.user_detail?.first_name || ''} ${timeOff.user_detail?.last_name || ''}`.trim() || timeOff.user_detail?.username || 'User'}
                            size="small"
                            color="error"
                            variant="outlined"
                            sx={{
                              display: 'block',
                              width: '100%',
                              fontSize: '0.7rem',
                              mb: 0.25,
                            }}
                          />
                        ))}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    </Paper>
  );
};

export default CalendarWeekView;
